import type { HeatMap, WeaponDefinition, Weapon } from '../../types';

export function createHeatMap(resolution: number, initial = 0): HeatMap {
  return { samples: new Array(resolution).fill(initial) };
}

export function setUniformHeat(map: HeatMap, value: number): void {
  // Apply slight cooling at the tips (positions 0 and 1) to model fire contact.
  const n = map.samples.length;
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const edgeFalloff = 1 - Math.min(1, Math.abs(t - 0.5) * 0.4); // 0.8 at ends, 1 in middle
    map.samples[i] = value * edgeFalloff;
  }
}

export function addLocalHeat(
  map: HeatMap,
  position: number,
  delta: number,
  sigma = 0.08,
): void {
  const n = map.samples.length;
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const d = t - position;
    const gauss = Math.exp(-(d * d) / (2 * sigma * sigma));
    map.samples[i]! += delta * gauss;
  }
}

export function cool(map: HeatMap, amount: number): void {
  const n = map.samples.length;
  for (let i = 0; i < n; i++) {
    map.samples[i] = Math.max(0, map.samples[i]! - amount);
  }
}

/** Mean heat across all samples. */
export function averageHeat(map: HeatMap): number {
  if (map.samples.length === 0) return 0;
  let s = 0;
  for (const v of map.samples) s += v;
  return s / map.samples.length;
}

export function maxHeat(map: HeatMap): number {
  let m = 0;
  for (const v of map.samples) if (v > m) m = v;
  return m;
}

export function cloneHeatMap(map: HeatMap): HeatMap {
  return { samples: [...map.samples] };
}

export function makeWeapon(
  def: WeaponDefinition,
  heat: HeatMap,
  damage: number[],
  quality: number,
  bonusDamage = 0,
  bonusDurability = 0,
): Weapon {
  return {
    id: `wpn_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
    type: def.type,
    shape: def.shape,
    heat,
    damage,
    quality,
    durability: Math.round(40 + quality * 60 + bonusDurability),
    damageStat: Math.round(10 + quality * 40 + bonusDamage),
    battlesWon: 0,
  };
}
